import type { L10n, Lang } from '@aera/schema';

/**
 * The consent sheet (section 10), shown in the session language before question A1. Two separate
 * screens, each with its own checkbox: a single checkbox on a long policy was ruled insufficient for
 * an overseas transfer by the Guangzhou Internet Court in 2024. The text version is stored with each
 * consent. Placeholders in {braces} are filled from config so a lawyer's edit never needs a deploy of
 * different wording per environment.
 *
 * To confirm with a PRC lawyer before the pilot (section 10, "Legal considerations").
 */
export const CONSENT_VERSION = 'v2-2026-11';

export interface ConsentSection {
  label: L10n;
  body: L10n;
}

export interface ConsentSheet {
  version: string;
  processing: {
    title: L10n;
    intro: L10n;
    sections: ConsentSection[];
    checkbox: L10n;
  };
  crossBorder: {
    title: L10n;
    body: L10n;
    /** Replaces the model sentence when the model runs on Aera's own server. */
    bodyLocalModel: L10n;
    checkbox: L10n;
    /** The checkbox when the model runs on Aera's own server: no overseas model to agree to. */
    checkboxLocalModel: L10n;
    declineHint: L10n;
  };
  versionLine: L10n;
}

export interface ConsentVars {
  entity: string;
  /** The official website, named with the entity ("谁在处理"). */
  website: string;
  contact: string;
  privacyEmail: string;
  /** "中国香港或新加坡，以最终选择为准" until the Atlas region decision is made. */
  atlasRegion: L10n;
  retentionMonths: number;
}

export const CONSENT_SHEET: ConsentSheet = {
  version: CONSENT_VERSION,
  processing: {
    title: { zh: '第一页 · 个人信息处理告知', en: 'Page 1 · How your information is handled' },
    intro: {
      zh: '您在本问卷中填写的内容，仅用于为贵司开展本次需求诊断。',
      en: 'What you enter in this questionnaire is used only for this needs diagnostic for your company.',
    },
    sections: [
      {
        label: { zh: '信息处理方', en: 'Who handles it' },
        body: { zh: '{entity}（官方网站：{website}；联系邮箱：{contact}）。', en: '{entity} (website: {website}; email: {contact}).' },
      },
      {
        label: { zh: '我们会储存哪些信息、询问哪些问题', en: 'What we store and what we ask' },
        body: {
          zh: '本问卷将询问贵司的业务概况、产品与目标市场、现有工作方式与获客渠道、主要痛点、期望目标、数据与系统现状、预算与决策安排等问题。我们会储存您的全部作答，以及您在问卷末尾自愿留下的称呼、公司名称、手机或微信、邮箱和方便联系的时间。请勿填写身份证号码、银行账户、健康信息或他人的个人信息。',
          en: 'The questionnaire asks about your business, your products and target markets, how the work and customer acquisition are done today, the main problems, your goals, your data and systems, budget and decision-making. We store all your answers, and the name, company name, phone or WeChat, email and preferred contact time you choose to leave at the end. Please do not enter ID numbers, bank details, health information or anyone else’s personal information.',
        },
      },
      {
        label: { zh: '使用目的', en: 'What it is used for' },
        body: {
          zh: '仅用于撰写需求简报、为贵司准备初步建议书及安排后续诊断沟通，不会用于其他目的，也不会提供给任何第三方。',
          en: 'Only to write your needs brief, prepare a preliminary proposal for your company and arrange the diagnostic conversation that follows. Nothing else, and it is never given to any third party.',
        },
      },
      {
        label: { zh: '查看权限', en: 'Who can see it' },
        body: { zh: '仅负责贵司的 Aera 顾问及一名系统管理员。', en: 'Only the Aera consultant responsible for your company and one administrator.' },
      },
      {
        label: { zh: '保存期限', en: 'How long it is kept' },
        body: {
          zh: '{retentionMonths} 个月后自动匿名化处理；您可随时要求删除。',
          en: 'Anonymised automatically after {retentionMonths} months. You can ask for deletion at any time.',
        },
      },
      {
        label: { zh: '您的权利', en: 'Your rights' },
        body: {
          zh: '您有权查阅、复制、更正、补充、删除您的个人信息，限制其处理或撤回同意。请发送邮件至 {privacyEmail}，我们将在 15 天内处理。',
          en: 'To access, copy, correct, complete or delete your information, restrict its processing, or withdraw consent: email {privacyEmail} and we act within 15 days.',
        },
      },
    ],
    checkbox: {
      zh: '本人已阅读并同意按上述方式处理本人信息（必选）',
      en: 'I have read this and agree to my information being handled as described (required)',
    },
  },
  crossBorder: {
    title: { zh: '第二页 · 个人信息境外存储与处理（需单独确认）', en: 'Page 2 · Storage and processing outside mainland China (confirmed separately)' },
    body: {
      zh: '为完成本次诊断，您的作答及联系方式将加密存储于境外服务器：MongoDB Atlas（{atlasRegion}）。您的作答还将发送至由 Ollama（美国）托管的 AI 模型，用于生成需求简报；您在问卷末尾填写的称呼、联系方式及公司名称不会发送至该模型，请勿在其他作答中填写此类信息。上述两家服务方均承诺不保存数据，也不将数据用于模型训练。您可随时撤回同意并要求删除相关信息：{privacyEmail}。',
      en: 'To prepare your diagnostic, your answers and contact details are stored encrypted on servers outside mainland China: MongoDB Atlas ({atlasRegion}). Your answers are also sent to an AI model hosted by Ollama (United States) to write the brief; the name, contact details and company name you leave at the end are not sent to it, so please do not include them in your other answers. Both providers commit to not keeping the data and not using it for training. You can withdraw consent and ask for deletion at any time: {privacyEmail}.',
    },
    bodyLocalModel: {
      zh: '为完成本次诊断，您的作答及联系方式将加密存储于境外服务器：MongoDB Atlas（{atlasRegion}）。AI 模型运行于 Aera 自有服务器，数据不会离开 Aera 的服务器。您可随时撤回同意并要求删除相关信息：{privacyEmail}。',
      en: "To prepare your diagnostic, your answers and contact details are stored encrypted on servers outside mainland China: MongoDB Atlas ({atlasRegion}). The AI model runs on Aera's own server, and your data does not leave Aera's servers. You can withdraw consent and ask for deletion at any time: {privacyEmail}.",
    },
    checkbox: {
      zh: '本人同意将上述信息传输至境外服务器存储，并由境外模型处理（必选；如不同意，可改为与顾问电话沟通，无需在线填写）',
      en: 'I agree to this information being transferred to and stored on servers outside mainland China and processed by a model hosted there (required; if you do not agree, you can talk it through with a consultant by phone instead)',
    },
    checkboxLocalModel: {
      zh: '本人同意将上述信息传输至境外服务器存储（必选；如不同意，可改为与顾问电话沟通，无需在线填写）',
      en: 'I agree to this information being transferred to and stored on servers outside mainland China (required; if you do not agree, you can talk it through with a consultant by phone instead)',
    },
    declineHint: {
      zh: '如您不同意，顾问可通过电话与您沟通同样的内容。',
      en: 'That is fine: a consultant can go through the same questions with you on a call.',
    },
  },
  versionLine: { zh: '版本 {version}', en: 'Version {version}' },
};

/** Fills the {placeholders} from config. */
export function renderConsent(text: L10n, lang: Lang, vars: ConsentVars & { version?: string }): string {
  return text[lang]
    .replaceAll('{entity}', vars.entity)
    .replaceAll('{website}', vars.website)
    .replaceAll('{contact}', vars.contact)
    .replaceAll('{privacyEmail}', vars.privacyEmail)
    .replaceAll('{atlasRegion}', vars.atlasRegion[lang])
    .replaceAll('{retentionMonths}', String(vars.retentionMonths))
    .replaceAll('{version}', vars.version ?? CONSENT_VERSION);
}

/** What the cross-border page names for each ATLAS_REGION (decision 3 in section 13). */
export const ATLAS_REGION_LABEL: Record<'hk' | 'sg', L10n> = {
  hk: { zh: '中国香港', en: 'Hong Kong' },
  sg: { zh: '新加坡', en: 'Singapore' },
};

export const DEFAULT_CONSENT_VARS: ConsentVars = {
  entity: 'Aera Cognition',
  website: 'aeracognition.com',
  contact: 'hello@aeracognition.com',
  privacyEmail: 'privacy@aeracognition.com',
  atlasRegion: { zh: '中国香港或新加坡，以最终选择为准', en: 'Hong Kong or Singapore, whichever is finally chosen' },
  retentionMonths: 12,
};

/** The chip on every question screen; it opens the sheet again. */
export const PRIVACY_CHIP: L10n = { zh: '仅 Aera 顾问可见 · 加密保存', en: 'Only your Aera consultant can see this · stored encrypted' };

/** Carried by the free-text screens (A1b, B4, I3 and their overseas counterparts). */
export const NO_NAMES_NOTE: L10n = {
  zh: '请勿填写本人或他人的姓名、公司名称及联系方式',
  en: "Please leave out names, company names and contact details (yours or anyone else's)",
};
